import { Fragment, useMemo, useState } from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";

import { useCan } from "@/lib/auth/useCan";

import {
  ActiveFilterPill,
  CheckboxList,
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
  type CheckboxOption,
} from "@/components/cinch/FilterBar";

import type { PipelineSearch } from "..";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { TierChip } from "@/components/cinch/TierChip";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  useForceSync,
  usePipeline,
  useRenewalsLiveRefresh,
  type PipelineRow,
} from "../api";
import { CustomerDrawer } from "../components/CustomerDrawer";
import { RefreshProgressDialog } from "../components/RefreshProgressDialog";
import {
  ForecastSelect,
  NotesField,
  PastForecastCell,
} from "../components/inline/InlineAnnotation";
import { LinkBadges, ResellerBadge } from "../components/pipeline/LinkBadges";
import { RenewalsSubNav } from "../components/SubNav";
import { fmtCur, fmtDate, fmtMRR, monthLabel, relativeAgo } from "../format";

const STATUS_VALUES = [
  "Active",
  "At Risk",
  "Midterm Churn",
  "Churned",
  "Booked Not Billed",
] as const;
const FORECAST_VALUES = ["Unknown", "Renew", "Renewal At Risk", "Churn"] as const;
const TIER_VALUES = [1, 2, 3, 4, 5] as const;

interface SortableCol {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
  /** Width hint via min-w-* class. */
  className?: string;
}

const COLUMNS: SortableCol[] = [
  { key: "ACCOUNT_NAME", label: "Account", className: "min-w-[230px]" },
  { key: "INDUSTRY_NAME", label: "Industry", className: "min-w-[120px]" },
  { key: "TIER", label: "Tier", align: "center", className: "min-w-[60px]" },
  { key: "CONTRACT_SIGNED_AT", label: "Signed", align: "right", className: "min-w-[84px]" },
  { key: "BILLING_START_MONTH", label: "Billing start", align: "right", className: "min-w-[96px]" },
  { key: "RENEWAL_MONTH", label: "Renewal", align: "right", className: "min-w-[90px]" },
  { key: "MONTHS_TO_RENEWAL", label: "Mo. out", align: "right", className: "min-w-[80px]" },
  { key: "CURRENT_MRR", label: "MRR", align: "right", className: "min-w-[92px]" },
  { key: "CURRENT_ARR", label: "ARR", align: "right", className: "min-w-[96px]" },
  { key: "LAST_BILLED_MONTH", label: "Last billed", align: "right", className: "min-w-[92px]" },
  { key: "BILLING_STATUS", label: "Billing status", align: "center", className: "min-w-[120px]" },
];
// Non-sortable trailing columns (CSM / forecast / notes inline editors).
const FIXED_COLS: { label: string; className?: string }[] = [
  { label: "CSM", className: "min-w-[140px]" },
  { label: "Forecast", className: "min-w-[140px]" },
  { label: "Notes", className: "min-w-[180px]" },
];
const TOTAL_COLS = COLUMNS.length + FIXED_COLS.length;

function effectiveArr(r: PipelineRow): number {
  const cur = Number(r.current_arr ?? 0);
  return cur > 0 ? cur : Number(r.last_billed_arr ?? 0);
}
function effectiveMrr(r: PipelineRow): number {
  const cur = Number(r.current_mrr ?? 0);
  return cur > 0 ? cur : Number(r.last_billed_mrr ?? 0);
}
function usingFallback(r: PipelineRow): boolean {
  return (r.current_mrr ?? 0) <= 0 && (r.last_billed_mrr ?? 0) > 0;
}

function buildWindow(months: number, view: "upcoming" | "past"): Set<string> {
  const set = new Set<string>();
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const [low, high] = view === "past" ? [-months, 0] : [-months, months];
  for (let d = low; d <= high; d++) {
    let mm = m + d;
    let yy = y;
    while (mm < 1) {
      mm += 12;
      yy--;
    }
    while (mm > 12) {
      mm -= 12;
      yy++;
    }
    set.add(`${yy}-${String(mm).padStart(2, "0")}`);
  }
  return set;
}

function rowMonth(r: PipelineRow, view: "upcoming" | "past"): string | null {
  const src = view === "past" ? r.last_renewal_month : (r.active_renewal_month ?? r.renewal_month);
  return src ? src.slice(0, 7) : null;
}

/** The forecast a row displays for the active view — the past cycle's recorded
 *  forecast in "past" view, the current cycle's live forecast otherwise. Keeps
 *  the filter chips + counts in lockstep with what the Forecast cell renders. */
function rowForecast(r: PipelineRow, view: "upcoming" | "past"): string {
  if (view === "past") return r.past_session?.forecast || "Unknown";
  return r.current_session?.forecast ?? r.annotation?.forecast ?? "Unknown";
}

function colValue(r: PipelineRow, col: string, view: "upcoming" | "past"): string | number {
  const isPast = view === "past";
  switch (col) {
    case "TIER":
      return r.current_tier ?? 99;
    case "RENEWAL_MONTH":
      return (
        (isPast ? r.last_renewal_month : (r.active_renewal_month ?? r.renewal_month)) ??
        "9999-99"
      );
    case "BILLING_START_MONTH":
      return r.first_billed_month ?? "9999-99";
    case "LAST_BILLED_MONTH":
      return r.last_billed_month ?? "9999-99";
    case "CONTRACT_SIGNED_AT":
      return r.contract_signed_at ?? "9999-99";
    case "MONTHS_TO_RENEWAL":
      return (
        (isPast
          ? r.months_since_last_renewal != null
            ? -r.months_since_last_renewal
            : Number.NEGATIVE_INFINITY
          : (r.active_months_to_renewal ?? r.months_to_renewal)) ?? Number.NEGATIVE_INFINITY
      );
    case "CURRENT_MRR":
      return effectiveMrr(r);
    case "CURRENT_ARR":
      return effectiveArr(r);
    case "BILLING_STATUS":
      return r.billing_status ?? "";
    case "INDUSTRY_NAME":
      return r.industry_name ?? "";
    case "ACCOUNT_NAME":
      return r.account_name ?? "";
    default:
      return "";
  }
}

function countBy<T>(arr: T[], keyFn: (t: T) => string | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of arr) {
    const k = keyFn(r);
    if (k == null) continue;
    out[k] = (out[k] ?? 0) + 1;
  }
  return out;
}

export function PipelinePage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/renewals/pipeline" }) as PipelineSearch;
  const canAdmin = useCan("renewals.admin");
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  useRenewalsLiveRefresh();

  const anchor: "billing" | "contract" = search.anchor ?? "billing";
  const view: "upcoming" | "past" = search.view ?? "upcoming";
  const showAll = !!search.showAll;
  const q = search.q ?? "";
  const sortCol = search.sort ?? "RENEWAL_MONTH";
  const sortDir: "asc" | "desc" = search.dir ?? "asc";

  // Memoized so the `?? []` fallback keeps a stable identity — these feed the
  // filtered-rows useMemo below, and a fresh array every render would defeat it.
  const filterStatus = useMemo(() => search.status ?? [], [search.status]);
  const filterForecast = useMemo(() => search.forecast ?? [], [search.forecast]);
  const filterCsm = useMemo(() => search.csm ?? [], [search.csm]);
  const filterMonth = useMemo(() => search.month ?? [], [search.month]);
  const filterIndustry = useMemo(() => search.industry ?? [], [search.industry]);
  const filterTier = useMemo(() => search.tier ?? [], [search.tier]);
  const filterCType = useMemo(() => search.ctype ?? [], [search.ctype]);
  // Setup mode: show only accounts that still need a first_term_start_date.
  // Drives the temporary "Setup" SubNav tab; also surfaces the inline banner.
  const setupMode = !!search.setup;

  const pipeline = usePipeline(anchor);
  const sync = useForceSync();

  const rows = useMemo<PipelineRow[]>(() => pipeline.data?.rows ?? [], [pipeline.data]);

  /** Update one search-param key in place (URL-driven state). */
  function setParam<K extends keyof PipelineSearch>(
    key: K,
    value: PipelineSearch[K] | undefined,
  ) {
    navigate({
      to: "/renewals/pipeline",
      search: (prev: PipelineSearch) => {
        const next: Record<string, unknown> = { ...prev };
        if (
          value === undefined ||
          value === "" ||
          value === false ||
          (Array.isArray(value) && value.length === 0)
        ) {
          delete next[key as string];
        } else {
          next[key as string] = value;
        }
        return next;
      },
      replace: true,
    });
  }
  function clearAll() {
    navigate({
      to: "/renewals/pipeline",
      // Drawer state is preserved by intention — clearing filters shouldn't
      // close an open account.
      search: (prev: PipelineSearch) => ({
        ...(prev.account != null ? { account: prev.account } : {}),
        ...(prev.expanded ? { expanded: true } : {}),
      }),
      replace: true,
    });
  }

  // ── Counts (computed on the full row set — users see population not result) ─
  const countsByStatus = countBy(rows, (r) => r.billing_status);
  const countsByForecast = countBy(rows, (r) => rowForecast(r, view));
  const countsByTier = countBy(rows, (r) => String(r.current_tier ?? 5));
  const countsByIndustry = countBy(rows, (r) => r.industry_name || "(none)");
  const countsByCType = countBy(rows, (r) => r.contract_type_name || "(none)");
  const countsByCsm = countBy(rows, (r) => {
    const id = r.annotation?.csm_user_id;
    return id ?? "unassigned";
  });

  const statusOptions: CheckboxOption[] = STATUS_VALUES.map((v) => ({
    value: v,
    label: v,
    count: countsByStatus[v] ?? 0,
  }));
  const forecastOptions: CheckboxOption[] = FORECAST_VALUES.map((v) => ({
    value: v,
    label: v,
    count: countsByForecast[v] ?? 0,
  }));
  const tierOptions: CheckboxOption[] = TIER_VALUES.map((t) => ({
    value: String(t),
    label: `T${t}`,
    count: countsByTier[String(t)] ?? 0,
  }));
  const industryOptions: CheckboxOption[] = useMemo(() => {
    const names = new Set<string>();
    for (const r of rows) if (r.industry_name) names.add(r.industry_name);
    return Array.from(names)
      .sort()
      .map((n) => ({ value: n, label: n, count: countsByIndustry[n] }));
  }, [rows, countsByIndustry]);
  const ctypeOptions: CheckboxOption[] = useMemo(() => {
    const names = new Set<string>();
    for (const r of rows) if (r.contract_type_name) names.add(r.contract_type_name);
    return Array.from(names)
      .sort()
      .map((n) => ({ value: n, label: n, count: countsByCType[n] }));
  }, [rows, countsByCType]);
  const monthOptions: CheckboxOption[] = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const m = rowMonth(r, view);
      if (m) counts.set(m, (counts.get(m) ?? 0) + 1);
    }
    return Array.from(counts.keys())
      .sort()
      .map((m) => ({ value: m, label: monthLabel(m + "-01") ?? m, count: counts.get(m) }));
  }, [rows, view]);
  const csmOptions: CheckboxOption[] = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (r.annotation?.csm_user_id && r.annotation.csm_display_name) {
        seen.set(r.annotation.csm_user_id, r.annotation.csm_display_name);
      }
    }
    const opts: CheckboxOption[] = [
      { value: "unassigned", label: "— Unassigned —", count: countsByCsm["unassigned"] ?? 0 },
    ];
    for (const [id, name] of seen) {
      opts.push({ value: id, label: name, count: countsByCsm[id] ?? 0 });
    }
    return opts;
  }, [rows, countsByCsm]);

  // ── Filter pass ────────────────────────────────────────────────────────
  const windowSet = useMemo(() => buildWindow(12, view), [view]);

  const filtered = useMemo(() => {
    const term = q.toLowerCase().trim();
    const sStatus = new Set(filterStatus);
    const sForecast = new Set(filterForecast);
    const sCsm = new Set(filterCsm);
    const sMonth = new Set(filterMonth);
    const sIndustry = new Set(filterIndustry);
    const sTier = new Set(filterTier);
    const sCtype = new Set(filterCType);

    return rows.filter((r) => {
      // Setup mode short-circuit: skip the renewal-window check entirely
      // (most needs-setup accounts have no usable renewal month anyway, and
      // a CSM in setup mode wants to see the whole backlog).
      if (setupMode) {
        if (r.is_merged) return false;
        if (r.annotation?.first_term_start_date) return false;
      }
      const isCustom =
        r.annotation?.renewal_anchor === "custom" && !!r.annotation?.renewal_date_override;
      const monthSrc = rowMonth(r, view);
      if (!setupMode && !showAll && !isCustom) {
        if (!monthSrc) return false;
        if (!windowSet.has(monthSrc)) return false;
      }
      if (sStatus.size && !sStatus.has(r.billing_status)) return false;
      const fc = rowForecast(r, view);
      if (sForecast.size && !sForecast.has(fc)) return false;
      if (sMonth.size) {
        if (!monthSrc || !sMonth.has(monthSrc)) return false;
      }
      if (sIndustry.size && !sIndustry.has(r.industry_name ?? "")) return false;
      if (sTier.size && !sTier.has(String(r.current_tier ?? 5))) return false;
      if (sCtype.size && !sCtype.has(r.contract_type_name ?? "")) return false;
      if (sCsm.size) {
        const key = r.annotation?.csm_user_id ?? "unassigned";
        if (!sCsm.has(key)) return false;
      }
      if (term) {
        const hay =
          `${r.account_name ?? ""} ${r.company_names ?? ""} ${r.qb_id} ${r.industry_name ?? ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [
    rows,
    q,
    filterStatus,
    filterForecast,
    filterCsm,
    filterMonth,
    filterIndustry,
    filterTier,
    filterCType,
    showAll,
    windowSet,
    view,
    setupMode,
  ]);

  // ── Sort pass ──────────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = colValue(a, sortCol, view);
      const vb = colValue(b, sortCol, view);
      if (typeof va === "string" && typeof vb === "string") {
        const r = va.localeCompare(vb);
        return sortDir === "asc" ? r : -r;
      }
      const r = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === "asc" ? r : -r;
    });
    return arr;
  }, [filtered, sortCol, sortDir, view]);

  const grouped = sortCol === "RENEWAL_MONTH";
  const groups = useMemo(() => {
    if (!grouped) return null;
    const map = new Map<string, PipelineRow[]>();
    for (const r of sorted) {
      const k = rowMonth(r, view) ?? "Unknown";
      const bucket = map.get(k);
      if (bucket) bucket.push(r);
      else map.set(k, [r]);
    }
    const keys = Array.from(map.keys()).sort((a, b) =>
      sortDir === "asc" ? a.localeCompare(b) : b.localeCompare(a),
    );
    return keys.map((m) => {
      const items = map.get(m)!;
      const arr = items.reduce((s, r) => s + effectiveArr(r), 0);
      return { ym: m, rows: items, arr };
    });
  }, [grouped, sorted, sortDir, view]);

  const onSort = (col: string) => {
    if (sortCol === col) {
      setParam("dir", sortDir === "asc" ? "desc" : "asc");
    } else {
      setParam("sort", col);
      setParam("dir", "asc");
    }
  };

  const monthCount = groups ? groups.length : new Set(sorted.map((r) => rowMonth(r, view))).size;
  const countLabel = grouped
    ? `${filtered.length} account${filtered.length !== 1 ? "s" : ""} · ${monthCount} renewal month${monthCount !== 1 ? "s" : ""}`
    : `${filtered.length} account${filtered.length !== 1 ? "s" : ""}`;

  // Active pills derived from the current filter sets so we can render the row
  // without threading every key explicitly.
  const monthLabelByKey = new Map(monthOptions.map((m) => [m.value, m.label]));
  const csmLabelByKey = new Map(csmOptions.map((c) => [c.value, c.label]));
  type Pill = { key: string; label: string; onRemove: () => void };
  const pills: Pill[] = [];
  for (const v of filterStatus)
    pills.push({
      key: `status:${v}`,
      label: `Billing status: ${v}`,
      onRemove: () => setParam("status", filterStatus.filter((x) => x !== v)),
    });
  for (const v of filterForecast)
    pills.push({
      key: `forecast:${v}`,
      label: `Forecast: ${v}`,
      onRemove: () => setParam("forecast", filterForecast.filter((x) => x !== v)),
    });
  for (const v of filterCsm)
    pills.push({
      key: `csm:${v}`,
      label: `CSM: ${csmLabelByKey.get(v) ?? v}`,
      onRemove: () => setParam("csm", filterCsm.filter((x) => x !== v)),
    });
  for (const v of filterMonth)
    pills.push({
      key: `month:${v}`,
      label: `Renewal: ${monthLabelByKey.get(v) ?? v}`,
      onRemove: () => setParam("month", filterMonth.filter((x) => x !== v)),
    });
  for (const v of filterIndustry)
    pills.push({
      key: `industry:${v}`,
      label: `Industry: ${v}`,
      onRemove: () => setParam("industry", filterIndustry.filter((x) => x !== v)),
    });
  for (const v of filterTier)
    pills.push({
      key: `tier:${v}`,
      label: `Tier: T${v}`,
      onRemove: () => setParam("tier", filterTier.filter((x) => x !== v)),
    });
  for (const v of filterCType)
    pills.push({
      key: `ctype:${v}`,
      label: `Contract: ${v}`,
      onRemove: () => setParam("ctype", filterCType.filter((x) => x !== v)),
    });
  if (showAll)
    pills.push({ key: "all", label: "All months", onRemove: () => setParam("showAll", undefined) });

  const hasAnyFilter =
    pills.length > 0 || q.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {setupMode ? "Renewals — Setup" : "Renewals — Pipeline"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {setupMode
            ? "Accounts that still need a first term start date. Setting an anchor here drops the row off this list."
            : "Every account with a master contract — driving anniversary, ARR, and CSM disposition."}
        </p>
      </div>
      <RenewalsSubNav />

      {setupMode && (
        <div className="rounded-md border border-info/30 bg-info-light/40 p-3 text-sm text-info">
          Set the first term start date for each account below to start tracking renewals.
          The tab disappears once every account is anchored.
        </div>
      )}

      <FilterBar>
        <FilterSearch
          placeholder="Search account, QB ID, industry…"
          value={q}
          onChange={(v) => setParam("q", v || undefined)}
        />
        <FilterChip label="Billing status" count={filterStatus.length}>
          <CheckboxList
            options={statusOptions}
            selected={filterStatus}
            onChange={(v) => setParam("status", v.length ? v : undefined)}
          />
        </FilterChip>
        <FilterChip label="Forecast" count={filterForecast.length}>
          <CheckboxList
            options={forecastOptions}
            selected={filterForecast}
            onChange={(v) => setParam("forecast", v.length ? v : undefined)}
          />
        </FilterChip>
        <FilterChip label="CSM" count={filterCsm.length}>
          <CheckboxList
            options={csmOptions}
            selected={filterCsm}
            onChange={(v) => setParam("csm", v.length ? v : undefined)}
            searchable
          />
        </FilterChip>
        <FilterChip label="Renewal" count={filterMonth.length}>
          <CheckboxList
            options={monthOptions}
            selected={filterMonth}
            onChange={(v) => setParam("month", v.length ? v : undefined)}
          />
        </FilterChip>
        <FilterChip label="Industry" count={filterIndustry.length}>
          <CheckboxList
            options={industryOptions}
            selected={filterIndustry}
            onChange={(v) => setParam("industry", v.length ? v : undefined)}
            searchable
          />
        </FilterChip>
        <FilterChip label="Tier" count={filterTier.length}>
          <CheckboxList
            options={tierOptions}
            selected={filterTier}
            onChange={(v) => setParam("tier", v.length ? v : undefined)}
          />
        </FilterChip>
        <FilterChip label="Contract" count={filterCType.length}>
          <CheckboxList
            options={ctypeOptions}
            selected={filterCType}
            onChange={(v) => setParam("ctype", v.length ? v : undefined)}
            searchable
          />
        </FilterChip>

        <div className="flex items-center gap-1 rounded-md border border-border p-0.5 text-xs">
          <ToggleBtn
            on={view === "upcoming"}
            onClick={() => setParam("view", undefined)}
            title="Show the next renewal date for each account"
          >
            Upcoming
          </ToggleBtn>
          <ToggleBtn
            on={view === "past"}
            onClick={() => setParam("view", "past")}
            title="Show the most recent past renewal anniversary"
          >
            Past
          </ToggleBtn>
        </div>
        <div className="flex items-center gap-1 rounded-md border border-border p-0.5 text-xs">
          <ToggleBtn on={anchor === "billing"} onClick={() => setParam("anchor", undefined)}>
            Billing
          </ToggleBtn>
          <ToggleBtn on={anchor === "contract"} onClick={() => setParam("anchor", "contract")}>
            Contract
          </ToggleBtn>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setParam("showAll", e.target.checked ? true : undefined)}
          />
          Show all months
        </label>

        <span className="ml-auto text-xs text-muted-foreground">
          {countLabel}
          {pipeline.data?.last_synced_at && <> · synced {relativeAgo(pipeline.data.last_synced_at)}</>}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          title="Invalidate the Snowflake pipeline + NRR caches and re-fetch."
        >
          {sync.isPending ? "Refreshing…" : "Refresh"}
        </Button>
        {canAdmin && (
          <Button
            variant="default"
            size="sm"
            onClick={() => setRefreshDialogOpen(true)}
            title="Run the full daily refresh on demand (cache + CSMs + contract metadata + session rollover)."
            className="gap-1"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Run daily refresh
          </Button>
        )}
      </FilterBar>

      <RefreshProgressDialog
        open={refreshDialogOpen}
        onOpenChange={setRefreshDialogOpen}
      />

      {hasAnyFilter && (
        <div className="flex flex-wrap items-center gap-1.5">
          {q && (
            <ActiveFilterPill label={`Search: "${q}"`} onRemove={() => setParam("q", undefined)} />
          )}
          {pills.map((p) => (
            <ActiveFilterPill key={p.key} label={p.label} onRemove={p.onRemove} />
          ))}
          <FilterClearAll onClick={clearAll} />
        </div>
      )}

      {pipeline.isLoading && <p className="text-sm text-muted-foreground">Loading pipeline…</p>}
      {pipeline.error && <p className="text-sm text-destructive">{String(pipeline.error)}</p>}

      {pipeline.data && (
        <Card>
          <CardContent className="p-0">
            <Table containerClassName="max-h-[calc(100vh-22rem)]">
              <TableHeader>
                <TableRow>
                  {COLUMNS.map((c) => (
                    <TableHead
                      key={c.key}
                      align={c.align ?? "left"}
                      sortable
                      sortDirection={sortCol === c.key ? sortDir : null}
                      onSort={() => onSort(c.key)}
                      className={c.className}
                    >
                      {c.key === "RENEWAL_MONTH" && view === "past"
                        ? "Last renewal"
                        : c.key === "MONTHS_TO_RENEWAL" && view === "past"
                          ? "Mo. ago"
                          : c.label}
                    </TableHead>
                  ))}
                  {FIXED_COLS.map((c) => (
                    <TableHead key={c.label} className={c.className}>
                      {c.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {grouped && groups
                  ? groups.map((g) => (
                      <Fragment key={g.ym}>
                        <MonthHeader ym={g.ym} arr={g.arr} count={g.rows.length} />
                        {g.rows.map((r) => (
                          <PipelineRowEl key={r.qb_id} r={r} view={view} navigate={navigate} />
                        ))}
                      </Fragment>
                    ))
                  : sorted.map((r) => (
                      <PipelineRowEl key={r.qb_id} r={r} view={view} navigate={navigate} />
                    ))}
              </TableBody>
            </Table>
            {sorted.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No accounts match the current filters.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <CustomerDrawer />
    </div>
  );
}

function ToggleBtn({
  on,
  onClick,
  children,
  title,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        "rounded px-2 py-1",
        on ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function MonthHeader({ ym, arr, count }: { ym: string; arr: number; count: number }) {
  const today = new Date();
  const ymToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const isCurrent = ym === ymToday;
  const isUnknown = ym === "Unknown";
  // Every group gets the brand-highlighted divider — visually equal-weight
  // month headers. The "This month" pill remains the cue for the current
  // cohort so the CSM can still find it at a glance.
  return (
    <TableRow className="border-t-2 border-brand hover:bg-transparent">
      <TableCell
        colSpan={TOTAL_COLS}
        className="bg-brand/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand"
      >
        <div className="flex items-center gap-2">
          <span>{isUnknown ? "No renewal date" : monthLabel(ym + "-01")}</span>
          {isCurrent && (
            <span className="rounded-full bg-brand px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
              This month
            </span>
          )}
          <span className="text-brand/70">·</span>
          <span className="tabular-nums">{fmtCur(arr)} ARR</span>
          <span className="text-brand/70">·</span>
          <span>{count} account{count !== 1 ? "s" : ""}</span>
        </div>
      </TableCell>
    </TableRow>
  );
}

function PipelineRowEl({
  r,
  view,
  navigate,
}: {
  r: PipelineRow;
  view: "upcoming" | "past";
  navigate: ReturnType<typeof useNavigate>;
}) {
  const isPast = view === "past";
  const isMerged = r.is_merged;
  const renewalMonth = isPast ? r.last_renewal_month : (r.active_renewal_month ?? r.renewal_month);
  const mtr = isPast
    ? r.months_since_last_renewal != null
      ? -r.months_since_last_renewal
      : null
    : (r.active_months_to_renewal ?? r.months_to_renewal);
  const mrr = effectiveMrr(r);
  const arr = effectiveArr(r);
  const dim = usingFallback(r);

  return (
    <TableRow
      clickable
      onClick={() =>
        navigate({
          to: "/renewals/pipeline",
          search: (prev: PipelineSearch) => ({ ...prev, account: r.qb_id }),
        })
      }
      className="align-top"
    >
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Two click affordances on the row:
                row click → quick view (TableRow.onClick below)
                name click → expanded view (this button, stopPropagation)
              This is intentional: the row carries the "peek" intent, the name
              carries the "I want the deep dive" intent. */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate({
                to: "/renewals/pipeline",
                search: (prev: PipelineSearch) => ({
                  ...prev,
                  account: r.qb_id,
                  expanded: true,
                }),
              });
            }}
            className="text-left font-medium text-brand hover:underline focus-visible:underline focus-visible:outline-none"
            title="Open full account view"
          >
            {r.account_name}
          </button>
          {r.is_reseller && <ResellerBadge />}
          <LinkBadges links={r.account_links} />
        </div>
        <div className="text-xs text-muted-foreground">QB-{r.qb_id}</div>
      </TableCell>
      <TableCell className="text-muted-foreground">{r.industry_name ?? "—"}</TableCell>
      <TableCell align="center">
        <TierChip tier={r.current_tier} />
      </TableCell>
      <TableCell align="right" className="text-muted-foreground">
        {fmtDate(r.contract_signed_at)}
      </TableCell>
      <TableCell align="right" className="text-muted-foreground">
        {fmtDate(r.first_billed_month)}
      </TableCell>
      <TableCell align="right" className="font-medium">{fmtDate(renewalMonth)}</TableCell>
      <TableCell align="right">
        {mtr == null
          ? "—"
          : isPast
            ? mtr === 0
              ? "This mo."
              : `${Math.abs(mtr)} mo ago`
            : mtr < 0
              ? `Renewed +${Math.abs(mtr)}m`
              : mtr === 0
                ? "This mo."
                : `${mtr} mo`}
      </TableCell>
      <TableCell
        numeric
        className={cn(dim && "text-muted-foreground")}
        title={dim && r.last_billed_month ? `Last billed ${r.last_billed_month}` : undefined}
      >
        {fmtMRR(mrr)}
      </TableCell>
      <TableCell
        numeric
        className={cn("font-medium", dim && "text-muted-foreground font-normal")}
      >
        {fmtCur(arr)}
      </TableCell>
      <TableCell align="right" className="text-muted-foreground">
        {fmtDate(r.last_billed_month)}
      </TableCell>
      <TableCell align="center">
        {isMerged ? (
          <span
            className="rounded-md border border-border bg-muted px-2 py-0.5 text-xs italic text-muted-foreground"
            title="Merged into another account; excluded from churn calculations."
          >
            N/A · Merged
          </span>
        ) : (
          <StatusBadge value={r.billing_status} />
        )}
      </TableCell>
      <TableCell>
        {r.annotation?.csm_display_name ? (
          <span>{r.annotation.csm_display_name}</span>
        ) : r.annotation?.csm_partner_name ? (
          <span className="inline-flex items-center gap-1">
            <span className="truncate">{r.annotation.csm_partner_name}</span>
            <span className="rounded bg-info-light px-1 text-[10px] font-semibold uppercase tracking-wide text-info">
              Partner
            </span>
          </span>
        ) : r.annotation?.csm_suggestion ? (
          <span className="text-xs italic text-muted-foreground">
            ?{r.annotation.csm_suggestion.display_name}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      {/* Stop propagation on inline editors so clicking the select / textarea
          doesn't fall through to the row-level drawer open. Editors are
          session-scoped (P7) — enabled once the account is anchored
          (first_term_start_date). The session row normally exists by then
          (created when the anchor is set), but if it hasn't materialized yet
          the first write creates it server-side, so gate on the anchor, not
          on current_session. Un-anchored rows read "set an anchor first" via
          the row-click drawer. */}
      {/* Past view is read-only: the cell shows the forecast/notes the CSM
          recorded for the cycle that renewed (past_session), not the account's
          current (next) cycle. Editors would target the wrong cycle here, so we
          don't stop propagation either — clicking through opens the drawer,
          where the full "Renewal cycles" history lives. */}
      <TableCell onClick={isPast ? undefined : (e) => e.stopPropagation()}>
        {isPast ? (
          <PastForecastCell session={r.past_session} />
        ) : r.current_session || r.annotation.first_term_start_date ? (
          <ForecastSelect
            qbId={r.qb_id}
            value={r.current_session?.forecast ?? r.annotation.forecast}
            disabled={isMerged}
          />
        ) : (
          <NoCycleHint />
        )}
      </TableCell>
      <TableCell
        onClick={isPast ? undefined : (e) => e.stopPropagation()}
        className="max-w-xs"
      >
        {isPast ? (
          r.past_session?.notes ? (
            <p className="whitespace-pre-wrap text-xs text-muted-foreground">
              {r.past_session.notes}
            </p>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )
        ) : r.current_session || r.annotation.first_term_start_date ? (
          <NotesField
            qbId={r.qb_id}
            value={r.current_session?.notes ?? r.annotation.notes}
            disabled={isMerged}
            rows={2}
          />
        ) : (
          <NoCycleHint />
        )}
      </TableCell>
    </TableRow>
  );
}

/** Placeholder shown in the Forecast / Notes pipeline cells when the row
 *  has no active renewal cycle. Editors are session-scoped (P7), so until
 *  a CSM anchors the account there's nothing to attach a forecast or note
 *  to. Clicking the row name opens the drawer where the anchor picker lives.
 */
function NoCycleHint() {
  return (
    <div className="flex items-center gap-1 text-xs italic text-muted-foreground">
      <span className="rounded-full bg-warning-light px-1.5 py-0.5 text-[10px] font-semibold not-italic uppercase tracking-wider text-warning">
        No anchor
      </span>
    </div>
  );
}
